import React, { useState, useMemo, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faSpinner,
  faTimes,
  faCalendarAlt,
  faSearch,
  faUser,
  faReceipt,
  faClock,
  faComment,
  faChevronLeft,
  faChevronRight,
  faClose,
  faCheckCircle,
  faMoneyBillWave,
  faUtensils,
  faBagShopping,
  faMotorcycle,
  faRefresh,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";

// Fetcher for voided items API
const voidedItemsFetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Items per page
const ITEMS_PER_PAGE = 20;

// Format currency
const formatCurrency = (amount) => {
  return `GH₵ ${parseFloat(amount || 0).toFixed(2)}`;
};

// Format date
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// Get order type info
const getOrderTypeInfo = (type) => {
  switch (type) {
    case "dine-in":
      return {
        icon: faUtensils,
        label: "Dine In",
        color: "#FF6B35",
        bgColor: "#FEF3F2",
      };
    case "takeaway":
      return {
        icon: faBagShopping,
        label: "Takeaway",
        color: "#10B981",
        bgColor: "#ECFDF5",
      };
    case "delivery":
      return {
        icon: faMotorcycle,
        label: "Delivery",
        color: "#3B82F6",
        bgColor: "#EFF6FF",
      };
    default:
      return {
        icon: faUtensils,
        label: "Dine In",
        color: "#FF6B35",
        bgColor: "#FEF3F2",
      };
  }
};

// ============ VOIDED ITEM DETAILS MODAL ============
const VoidedItemDetailsModal = ({ voidedItem, onClose }) => {
  if (!voidedItem) return null;

  const orderType = getOrderTypeInfo(voidedItem.order_type);

  return (
    <div className="modal" style={modalStyles.overlay}>
      <div className="overlay" onClick={onClose}></div>
      <div className="modal-content" style={modalStyles.content}>
        <div style={modalStyles.header}>
          <div>
            <h2 style={modalStyles.title}>
              <FontAwesomeIcon
                icon={faBan}
                style={{ marginRight: "12px", color: "#DC2626" }}
              />
              Voided Item Details
            </h2>
            <div style={modalStyles.metaInfo}>
              <span style={modalStyles.metaBadge}>
                Order #{voidedItem.order_id || "N/A"}
              </span>
              {orderType && (
                <span
                  style={{
                    ...modalStyles.typeBadge,
                    backgroundColor: orderType.bgColor,
                    color: orderType.color,
                  }}
                >
                  <FontAwesomeIcon
                    icon={orderType.icon}
                    style={{ marginRight: "6px" }}
                  />
                  {orderType.label}
                </span>
              )}
              {voidedItem.table_number && (
                <span style={modalStyles.metaBadge}>
                  Table {voidedItem.table_number}
                </span>
              )}
              {voidedItem.guest_name && (
                <span style={modalStyles.metaBadge}>
                  Customer: {voidedItem.guest_name}
                </span>
              )}
            </div>
          </div>
          <button style={modalStyles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.itemCard}>
            <div style={modalStyles.itemHeader}>
              <h3 style={modalStyles.itemName}>
                {voidedItem.item?.name || "Unknown"}
              </h3>
              <span style={modalStyles.itemStatus}>VOIDED</span>
            </div>

            <div style={modalStyles.itemDetails}>
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>Quantity Voided:</span>
                <span style={modalStyles.detailValue}>
                  {voidedItem.item?.quantity || 0}
                </span>
              </div>
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>Unit Price:</span>
                <span style={modalStyles.detailValue}>
                  {formatCurrency(voidedItem.item?.price || 0)}
                </span>
              </div>
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>
                  Total Voided Amount:
                </span>
                <span
                  style={{
                    ...modalStyles.detailValue,
                    color: "#DC2626",
                    fontWeight: "700",
                  }}
                >
                  {formatCurrency(
                    (voidedItem.item?.price || 0) *
                      (voidedItem.item?.quantity || 0),
                  )}
                </span>
              </div>
              {voidedItem.item?.notes && (
                <div style={modalStyles.detailRow}>
                  <span style={modalStyles.detailLabel}>Item Notes:</span>
                  <span style={modalStyles.detailValue}>
                    {voidedItem.item.notes}
                  </span>
                </div>
              )}
            </div>

            <div style={modalStyles.divider} />

            <div style={modalStyles.voidInfo}>
              <h4 style={modalStyles.voidSectionTitle}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ marginRight: "8px", color: "#FF6B35" }}
                />
                Void Information
              </h4>

              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>
                  <FontAwesomeIcon
                    icon={faUser}
                    style={{ marginRight: "6px", color: "#6B7280" }}
                  />
                  Voided By:
                </span>
                <span style={{ ...modalStyles.detailValue, fontWeight: "600" }}>
                  {voidedItem.item?.voided_by || "Unknown"}
                </span>
              </div>

              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>
                  <FontAwesomeIcon
                    icon={faUser}
                    style={{ marginRight: "6px", color: "#6B7280" }}
                  />
                  Username:
                </span>
                <span style={modalStyles.detailValue}>
                  {voidedItem.item?.voided_by_username || "N/A"}
                </span>
              </div>

              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>
                  <FontAwesomeIcon
                    icon={faClock}
                    style={{ marginRight: "6px", color: "#6B7280" }}
                  />
                  Voided At:
                </span>
                <span style={modalStyles.detailValue}>
                  {formatDateTime(
                    voidedItem.item?.voided_at || voidedItem.order_created,
                  )}
                </span>
              </div>

              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>
                  <FontAwesomeIcon
                    icon={faComment}
                    style={{ marginRight: "6px", color: "#6B7280" }}
                  />
                  Reason:
                </span>
                <span style={{ ...modalStyles.detailValue, color: "#FF6B35" }}>
                  {voidedItem.item?.void_reason || "No reason provided"}
                </span>
              </div>
            </div>

            <div style={modalStyles.divider} />

            <div style={modalStyles.orderInfo}>
              <h4 style={modalStyles.voidSectionTitle}>
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ marginRight: "8px", color: "#4CAF50" }}
                />
                Order Information
              </h4>
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>Order Status:</span>
                <span style={modalStyles.detailValue}>
                  {voidedItem.order_status?.toUpperCase() || "N/A"}
                </span>
              </div>
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>Waiter/Server:</span>
                <span style={modalStyles.detailValue}>
                  {voidedItem.waiter_name || "N/A"}
                </span>
              </div>
              {voidedItem.cashier_name && (
                <div style={modalStyles.detailRow}>
                  <span style={modalStyles.detailLabel}>Cashier:</span>
                  <span style={modalStyles.detailValue}>
                    {voidedItem.cashier_name}
                  </span>
                </div>
              )}
              <div style={modalStyles.detailRow}>
                <span style={modalStyles.detailLabel}>Order Date:</span>
                <span style={modalStyles.detailValue}>
                  {formatDateTime(voidedItem.order_created)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.closeModalButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MODAL STYLES ============
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  content: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "650px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    overflow: "hidden",
  },
  header: {
    padding: "24px 28px",
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    fontSize: "20px",
    fontWeight: "700",
    margin: 0,
    color: "#1F2937",
  },
  metaInfo: {
    display: "flex",
    gap: "8px",
    marginTop: "8px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  typeBadge: {
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  metaBadge: {
    padding: "4px 12px",
    backgroundColor: "#F3F4F6",
    borderRadius: "20px",
    fontSize: "12px",
    color: "#4B5563",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#9CA3AF",
    padding: "8px",
    borderRadius: "8px",
    transition: "background 0.2s",
  },
  body: {
    padding: "24px 28px",
    overflowY: "auto",
    flex: 1,
  },
  itemCard: {
    backgroundColor: "#FEF2F2",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #FECACA",
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  itemName: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1F2937",
    margin: 0,
  },
  itemStatus: {
    backgroundColor: "#DC2626",
    color: "white",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  itemDetails: {
    marginBottom: "16px",
  },
  detailRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "6px",
    fontSize: "14px",
    alignItems: "center",
  },
  detailLabel: {
    fontWeight: "500",
    color: "#6B7280",
    minWidth: "160px",
  },
  detailValue: {
    color: "#1F2937",
  },
  divider: {
    height: "1px",
    backgroundColor: "#E5E7EB",
    margin: "16px 0",
  },
  voidInfo: {
    marginBottom: "16px",
  },
  orderInfo: {
    marginBottom: "4px",
  },
  voidSectionTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#1F2937",
    margin: "0 0 12px 0",
  },
  footer: {
    padding: "16px 28px",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  closeModalButton: {
    padding: "10px 24px",
    backgroundColor: "#F3F4F6",
    color: "#4B5563",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s",
  },
};

export default function VoidRegister() {
  const { stateNavigator } = useNavigationEvent();

  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWaiter, setSelectedWaiter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showVoidDetails, setShowVoidDetails] = useState(false);
  const [selectedVoidedItem, setSelectedVoidedItem] = useState(null);

  // Format date for API
  const formatDateForAPI = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getApiUrl = () => {
    const startDateParam = formatDateForAPI(startDate);
    const endDateParam = formatDateForAPI(endDate);

    if (startDateParam === endDateParam) {
      return `${process.env.REACT_APP_BASE_URL}/api/voided-items?date=${startDateParam}`;
    }
    return `${process.env.REACT_APP_BASE_URL}/api/voided-items?from_date=${startDateParam}&to_date=${endDateParam}`;
  };

  const {
    data: response,
    isLoading,
    error,
    mutate,
  } = useSWR(getApiUrl(), voidedItemsFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 30000,
  });

  // The API returns data directly in the response.data array
  const voidedItems = response?.data || [];

  // Get unique waiters from voided items
  const waiterOptions = useMemo(() => {
    const waiters = new Set();
    voidedItems.forEach((item) => {
      const waiterName = item.waiter_name || "";
      if (waiterName) {
        waiters.add(waiterName);
      }
    });
    return Array.from(waiters).sort();
  }, [voidedItems]);

  // Get unique cashiers from voided items
  const cashierOptions = useMemo(() => {
    const cashiers = new Set();
    voidedItems.forEach((item) => {
      const cashierName = item.cashier_name || "";
      if (cashierName) {
        cashiers.add(cashierName);
      }
    });
    return Array.from(cashiers).sort();
  }, [voidedItems]);

  // Filter voided items
  const filteredItems = useMemo(() => {
    if (!voidedItems.length) return [];

    let filtered = [...voidedItems];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const waiterName = item.waiter_name || "";
        const cashierName = item.cashier_name || "";
        const itemName = item.item?.name || "";
        const guestName = item.guest_name || "";
        const voidedBy = item.item?.voided_by || "";
        const voidedByUsername = item.item?.voided_by_username || "";

        return (
          guestName.toLowerCase().includes(searchLower) ||
          item.order_id.toString().includes(searchTerm) ||
          itemName.toLowerCase().includes(searchLower) ||
          waiterName.toLowerCase().includes(searchLower) ||
          cashierName.toLowerCase().includes(searchLower) ||
          voidedBy.toLowerCase().includes(searchLower) ||
          voidedByUsername.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by waiter
    if (selectedWaiter !== "all") {
      filtered = filtered.filter((item) => {
        const waiterName = item.waiter_name || "";
        return waiterName === selectedWaiter;
      });
    }

    return filtered;
  }, [voidedItems, searchTerm, selectedWaiter]);

  // Pagination
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredItems.slice(start, end);
  }, [filteredItems, currentPage]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedWaiter, startDate, endDate]);

  // Statistics
  const statistics = useMemo(() => {
    if (!filteredItems.length) {
      return {
        totalVoidedItems: 0,
        totalVoidedAmount: 0,
        uniqueCustomers: 0,
        uniqueWaiters: 0,
        uniqueCashiers: 0,
        uniqueOrders: 0,
      };
    }

    let totalVoidedItems = 0;
    let totalVoidedAmount = 0;
    const customers = new Set();
    const waiters = new Set();
    const cashiers = new Set();
    const orders = new Set();

    filteredItems.forEach((item) => {
      const qty = item.item?.quantity || 0;
      const price = item.item?.price || 0;
      totalVoidedItems += qty;
      totalVoidedAmount += price * qty;

      if (item.guest_name && item.guest_name !== "Guest") {
        customers.add(item.guest_name);
      }

      if (item.waiter_name) waiters.add(item.waiter_name);
      if (item.cashier_name) cashiers.add(item.cashier_name);
      if (item.order_id) orders.add(item.order_id);
    });

    return {
      totalVoidedItems,
      totalVoidedAmount,
      uniqueCustomers: customers.size,
      uniqueWaiters: waiters.size,
      uniqueCashiers: cashiers.size,
      uniqueOrders: orders.size,
    };
  }, [filteredItems]);

  const handleRefresh = async () => {
    setCurrentPage(1);
    await mutate();
  };

  const handleStartDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(0, 0, 0, 0);
    setStartDate(newDate);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(23, 59, 59, 999);
    setEndDate(newDate);
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (isLoading && !response) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading voided items...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error && !response) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Data</h3>
              <p>Unable to connect to the API. Please check your connection.</p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus
            AddTitle="Voided Items Register"
            buttonLabel="Cash Register"
            // icon={faReorder}
            onClick={() => stateNavigator.navigate("cashregister")}
          />

          {/* STATISTICS CARDS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faBan}
                  style={{ color: "#DC2626", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalVoidedItems}</h3>
                <p style={styles.statLabel}>Total Voided Items</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FEE2E2",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#DC2626", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalVoidedAmount)}
                </h3>
                <p style={styles.statLabel}>Total Voided Amount</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.uniqueOrders}</h3>
                <p style={styles.statLabel}>Affected Orders</p>
              </div>
            </div>
          </div>

          {/* FILTERS BAR */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by order #, customer, item, user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <label style={styles.dateLabel}>From:</label>
              <input
                type="date"
                style={styles.dateInput}
                value={startDate.toISOString().split("T")[0]}
                onChange={handleStartDateChange}
              />
            </div>
            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <label style={styles.dateLabel}>To:</label>
              <input
                type="date"
                style={styles.dateInput}
                value={endDate.toISOString().split("T")[0]}
                onChange={handleEndDateChange}
              />
            </div>

            <select
              style={styles.filterSelect}
              value={selectedWaiter}
              onChange={(e) => setSelectedWaiter(e.target.value)}
            >
              <option value="all">All Waiters</option>
              {waiterOptions.map((waiter) => (
                <option key={waiter} value={waiter}>
                  {waiter}
                </option>
              ))}
            </select>
          </div>

          {/* VOIDED ITEMS TABLE */}
          {filteredItems.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faBan}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Voided Items Found</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm || selectedWaiter !== "all"}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.tableCard}>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Order #</th>
                        <th style={styles.th}>Voided Date</th>
                        <th style={styles.th}>Customer</th>
                        <th style={styles.th}>Item Name</th>
                        <th style={styles.th}>Qty</th>
                        <th style={styles.th}>Voided Amount</th>
                        <th style={styles.th}>Voided By</th>
                        <th style={styles.th}>Waiter</th>
                        <th style={styles.th}>Order Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedItems.map((item, index) => {
                        return (
                          <tr key={index} style={styles.tableRow}>
                            <td style={styles.td}>#{item.order_id || "N/A"}</td>
                            <td style={styles.td}>
                              {formatDateTime(
                                item.item?.voided_at || item.order_created,
                              )}
                            </td>
                            <td style={styles.td}>
                              {item.guest_name || "Guest"}
                            </td>
                            <td style={styles.td}>
                              <strong>{item.item?.name || "Unknown"}</strong>
                              {item.item?.notes && (
                                <span style={styles.itemNotes}>
                                  {" "}
                                  ({item.item.notes})
                                </span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={styles.voidedCountBadge}>
                                {item.item?.quantity || 0}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span
                                style={{ color: "#DC2626", fontWeight: "600" }}
                              >
                                {formatCurrency(
                                  (item.item?.price || 0) *
                                    (item.item?.quantity || 0),
                                )}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={{ fontWeight: "500" }}>
                                {item.item?.voided_by || "Unknown"}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={{ color: "#6B7280" }}>
                                {item.waiter_name || "N/A"}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={{ color: "#FF6B35" }}>
                                {item.order_type}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </button>
                  <span style={styles.paginationInfo}>
                    Page {currentPage} of {totalPages} ({filteredItems.length}{" "}
                    items)
                  </span>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === totalPages ? 0.5 : 1,
                    }}
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* VOIDED ITEM DETAILS MODAL */}
      {showVoidDetails && selectedVoidedItem && (
        <VoidedItemDetailsModal
          voidedItem={selectedVoidedItem}
          onClose={() => {
            setShowVoidDetails(false);
            setSelectedVoidedItem(null);
          }}
        />
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// ============ STYLES ============
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  statIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "13px",
  },
  filtersBar: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  dateWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dateLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#4B5563",
  },
  dateInput: {
    border: "none",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1F2937",
    outline: "none",
    backgroundColor: "transparent",
    padding: "4px",
    minWidth: "130px",
  },
  searchWrapper: {
    flex: 2,
    position: "relative",
    minWidth: "250px",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9CA3AF",
  },
  searchInput: {
    width: "100%",
    padding: "12px 16px 12px 40px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    fontSize: "15px",
    outline: "none",
    backgroundColor: "white",
  },
  filterSelect: {
    padding: "12px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "160px",
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1200px",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: "15px",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: "15px",
    color: "#1F2937",
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#F8FAFC",
    },
  },
  orderTypeBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "15px",
    fontWeight: "500",
  },
  voidedCountBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    padding: "2px 10px",
    borderRadius: "20px",
    fontSize: "15px",
    fontWeight: "600",
    minWidth: "30px",
  },
  itemNotes: {
    color: "#FF6B35",
    fontSize: "15px",
    fontStyle: "italic",
    marginLeft: "4px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "20px",
    padding: "16px",
    backgroundColor: "white",
    borderRadius: "12px",
  },
  paginationButton: {
    padding: "8px 16px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  paginationInfo: {
    fontSize: "15px",
    color: "#6B7280",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "15px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
};
